import { useEffect, useState } from 'react';
import { WifiOff, RefreshCw, CloudUpload } from 'lucide-react';
import { subscribeOfflineQueue, drainQueue } from '@/services/offlineQueue';
import { toast } from 'sonner';

const STORAGE_KEY = 'kk-last-sync';

const fmtTime = (ts) => {
  if (!ts) return null;
  try {
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return null;
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    return isToday
      ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      : d.toLocaleString([], { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return null;
  }
};

/**
 * Floating banner that appears whenever the browser is offline. Reads the
 * "last sync" timestamp written by the service worker (every successful
 * cache-eligible API GET broadcasts a kk-sync message) so users know how
 * fresh the data they're seeing is.
 */
export const OfflineBanner = () => {
  const [online, setOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [lastSync, setLastSync] = useState(() => {
    try { return Number(localStorage.getItem(STORAGE_KEY)) || null; } catch { return null; }
  });
  const [pending, setPending] = useState(0);

  useEffect(() => subscribeOfflineQueue((q) => setPending(q.length)), []);

  useEffect(() => {
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const handler = (event) => {
      if (event?.data?.type === 'kk-sync' && typeof event.data.at === 'number') {
        try { localStorage.setItem(STORAGE_KEY, String(event.data.at)); } catch { /* ignore */ }
        setLastSync(event.data.at);
      }
    };
    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
  }, []);

  if (online && pending === 0) return null;

  const synced = fmtTime(lastSync);

  // Online with pending mutations queued → green-ish "syncing" pill
  if (online && pending > 0) {
    return (
      <div
        role="status"
        aria-live="polite"
        data-testid="pending-sync-banner"
        className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 rounded-full bg-emerald-100 border border-emerald-300 px-4 py-2 shadow-lg max-w-[calc(100vw-32px)]"
      >
        <CloudUpload className="h-4 w-4 text-emerald-700" />
        <span className="text-sm font-semibold text-emerald-900">
          {pending} pending sync{pending !== 1 ? 's' : ''}
        </span>
        <button
          type="button"
          onClick={() => drainQueue(toast)}
          className="ml-1 inline-flex items-center gap-1 rounded-full bg-white text-emerald-900 hover:bg-emerald-50 text-[11px] font-semibold px-2.5 py-1 border border-emerald-300"
          data-testid="sync-now-btn"
        >
          <RefreshCw className="h-3 w-3" /> Sync now
        </button>
      </div>
    );
  }

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="offline-banner"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 rounded-full bg-amber-100 border border-amber-300 px-4 py-2 shadow-lg max-w-[calc(100vw-32px)]"
    >
      <span className="relative flex h-2.5 w-2.5">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500" />
      </span>
      <WifiOff className="h-4 w-4 text-amber-700" />
      <div className="flex flex-col leading-tight">
        <span className="text-sm font-semibold text-amber-900">You're offline</span>
        <span className="text-[11px] text-amber-700">
          {pending > 0 ? `${pending} action${pending !== 1 ? 's' : ''} queued · ` : ''}
          {synced ? `Last sync ${synced}` : 'Showing cached data'}
        </span>
      </div>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="ml-1 inline-flex items-center gap-1 rounded-full bg-white text-amber-900 hover:bg-amber-50 text-[11px] font-semibold px-2.5 py-1 border border-amber-300"
        data-testid="offline-retry-btn"
      >
        <RefreshCw className="h-3 w-3" /> Retry
      </button>
    </div>
  );
};

export default OfflineBanner;
